import * as React from "react";
import { Info } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

// Tiny, accessible info tooltip. Reuses the Radix tooltip (hover/focus on desktop,
// tap on mobile). `as="span"` variant is used when nested inside an interactive
// element (e.g. a clickable KPI card) to avoid invalid button-in-button nesting.
export function InfoHint({ text, label, as = "button", className, testid = "info-hint" }) {
  const [open, setOpen] = React.useState(false);
  const stop = (e) => e.stopPropagation();

  const common = {
    "aria-label": label,
    "data-testid": testid,
    className: cn(
      "inline-flex items-center justify-center rounded-full align-middle opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-current/40",
      className
    ),
    onPointerDown: stop,
    onPointerEnter: () => setOpen(true),
    onPointerLeave: () => setOpen(false),
    onFocus: () => setOpen(true),
    onBlur: () => setOpen(false),
    onClick: (e) => { stop(e); setOpen(true); },
  };
  const icon = <Info className="h-3.5 w-3.5" aria-hidden="true" />;

  const trigger =
    as === "span" ? (
      <span
        role="button"
        tabIndex={0}
        {...common}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            e.stopPropagation();
            setOpen((v) => !v);
          }
        }}
      >
        {icon}
      </span>
    ) : (
      <button type="button" {...common}>
        {icon}
      </button>
    );

  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip open={open} onOpenChange={setOpen}>
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        <TooltipContent side="top" className="max-w-[220px] whitespace-normal text-center leading-snug" data-testid={`${testid}-content`}>
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
